import { useCallback, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import { useUserState } from "../../../state/user";
import {
  PhoneNumberInput,
  updateCmdPhone,
  updateProfessionalPhone
} from "../../../services/professionals";
import {
  DEFAULT_COUNTRY_CODE,
  countryOptions,
  isValidLocalPhoneNumber
} from "../../../utils/countries";
import { outlinedFieldSx } from "../../../utils/fieldStyles";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

const sectionSx = { mb: 3 };

const labelSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary,
  mb: 1
};

const noteSx = {
  ...typography.body.s,
  color: semanticColors.text.secondary,
  mt: 0.5
};

export interface PhoneValue {
  countryPrefix?: string;
  number?: string;
}

/** One number: the country it belongs to, and the national number itself. */
function PhoneFields({
  title,
  note,
  value,
  onChange,
  error
}: {
  title: string;
  note: string;
  value: PhoneNumberInput;
  onChange: (value: PhoneNumberInput) => void;
  error?: string;
}) {
  const { t } = useTranslation();

  return (
    <Box sx={sectionSx}>
      <Typography sx={labelSx}>{title}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={5}>
          <TextField
            select
            label={t("paymentConfigurationForm.phoneCountry")}
            value={value.countryPrefix}
            onChange={event =>
              onChange({ ...value, countryPrefix: event.target.value })
            }
            fullWidth
            sx={outlinedFieldSx}
          >
            {countryOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={7}>
          <TextField
            label={t("professionalData.phoneNumber")}
            value={value.number}
            onChange={event =>
              onChange({ ...value, number: event.target.value })
            }
            error={Boolean(error)}
            helperText={error ?? ""}
            fullWidth
            inputProps={{ inputMode: "tel" }}
            sx={outlinedFieldSx}
          />
        </Grid>
      </Grid>
      <Typography sx={noteSx}>{note}</Typography>
    </Box>
  );
}

/**
 * The professional's two non-personal numbers.
 *
 * They are separate endpoints, and only the ones actually changed are sent —
 * both are update-only server side, so a professional who never registered one
 * would get a 404 for touching it needlessly.
 *
 * Validation is local, against the country picked, and the country travels as
 * an ISO code: the API rejects dialling codes outright, since "+351" does not
 * identify a country on its own.
 */
export default function UpdateContactsDialog({
  open,
  onClose,
  cmdPhone,
  professionalPhone,
  onUpdated
}: {
  open: boolean;
  onClose: () => void;
  cmdPhone?: PhoneValue;
  professionalPhone?: PhoneValue;
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const { token, refreshUser } = useUserState();

  const asInput = (phone?: PhoneValue): PhoneNumberInput => ({
    countryPrefix: phone?.countryPrefix ?? DEFAULT_COUNTRY_CODE,
    number: phone?.number ?? ""
  });

  const [cmd, setCmd] = useState<PhoneNumberInput>(() => asInput(cmdPhone));
  const [practice, setPractice] = useState<PhoneNumberInput>(() =>
    asInput(professionalPhone)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalid = (phone: PhoneNumberInput) =>
    phone.number.trim().length > 0 &&
    !isValidLocalPhoneNumber(phone.countryPrefix, phone.number);

  const cmdError = invalid(cmd)
    ? t("professionalData.phoneInvalid")
    : undefined;
  const practiceError = invalid(practice)
    ? t("professionalData.phoneInvalid")
    : undefined;

  const changed = (phone: PhoneNumberInput, original?: PhoneValue) =>
    phone.number.trim().length > 0 &&
    (phone.number !== (original?.number ?? "") ||
      phone.countryPrefix !==
        (original?.countryPrefix ?? DEFAULT_COUNTRY_CODE));

  const cmdChanged = changed(cmd, cmdPhone);
  const practiceChanged = changed(practice, professionalPhone);
  const canSubmit =
    (cmdChanged || practiceChanged) && !cmdError && !practiceError && !saving;

  const onSubmit = useCallback(async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      if (cmdChanged) await updateCmdPhone({ authToken: token, phone: cmd });
      if (practiceChanged) {
        await updateProfessionalPhone({ authToken: token, phone: practice });
      }
      await refreshUser();
      onUpdated();
    } catch {
      setError(t("professionalData.contactsError"));
    } finally {
      setSaving(false);
    }
  }, [
    token,
    cmd,
    practice,
    cmdChanged,
    practiceChanged,
    refreshUser,
    onUpdated,
    t
  ]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={t("professionalData.updateContacts")}
      maxWidth="sm"
    >
      <DialogContent>
        <PhoneFields
          title={t("professionalData.cmdPhone")}
          note={t("professionalData.cmdPhoneNote")}
          value={cmd}
          onChange={setCmd}
          error={cmdError}
        />
        <PhoneFields
          title={t("professionalData.professionalPhone")}
          note={t("professionalData.professionalPhoneNote")}
          value={practice}
          onChange={setPractice}
          error={practiceError}
        />

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("professionalData.update")}
            onClick={onSubmit}
            loading={saving}
            disabled={!canSubmit}
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
